import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaUser } from 'react-icons/fa';

// Animation for the notification to slide in from bottom
const slideIn = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

// Animation for the notification to slide out to bottom
const slideOut = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(100%);
    opacity: 0;
  }
`;

const BubbleContainer = styled.div`
  position: fixed;
  left: 20px;
  bottom: 20px;
  z-index: 1000;
  animation: ${props => props.$isVisible ? slideIn : slideOut} 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
  max-width: 300px;
  
  @media (max-width: 768px) {
    left: 10px;
    bottom: 10px;
    max-width: 250px;
  }
`;

const GlassBubble = styled.div`
  background: rgba(13, 38, 45, 0.4);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-radius: 15px;
  padding: 15px;
  
  @media (max-width: 768px) {
    padding: 12px;
  }
  box-shadow: 0 8px 32px rgba(0, 225, 199, 0.25);
  border: 1px solid rgba(0, 225, 199, 0.15);
  display: flex;
  align-items: center;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 8px 32px rgba(0, 225, 199, 0.4);
    border: 1px solid rgba(0, 225, 199, 0.3);
  }
`;

const IconContainer = styled.div`
  width: 40px;
  height: 40px;
  
  @media (max-width: 768px) {
    width: 35px;
    height: 35px;
  }
  border-radius: 50%;
  background: linear-gradient(145deg, #00e1c7, #0c8a7d);
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 0 10px rgba(0, 225, 199, 0.5);
  margin-right: 12px;
  flex-shrink: 0;
  
  svg {
    color: white;
    font-size: 20px;
    
    @media (max-width: 768px) {
      font-size: 16px;
    }
  }
`;

const ContentContainer = styled.div`
  flex: 1;
`;

const UserName = styled.div`
  font-weight: bold;
  color: #00e1c7;
  text-shadow: 0 0 5px rgba(0, 225, 199, 0.3);
  margin-bottom: 4px;
  font-size: 0.95rem;
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const Message = styled.div`
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.85rem;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    line-height: 1.3;
  }
`;

const TimeAgo = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.75rem;
  
  @media (max-width: 768px) {
    font-size: 0.7rem;
  }
  margin-top: 4px;
`;

// Sample notification data in English, Hinglish, and Hindi
const notificationData = [
  {
    name: "Rishi",
    city: "Delhi",
    message: "saved 25 minutes finding parking using ParkGanga",
    timeAgo: "2 minutes ago"
  },
  {
    name: "Priya",
    city: "Mumbai",
    message: "ne ParkGanga app se 15 minute ki parking dhoondh li",
    timeAgo: "5 minutes ago"
  },
  {
    name: "Amit",
    city: "Bangalore",
    message: "ने पार्कगंगा ऐप से 20 मिनट बचाए",
    timeAgo: "just now"
  },
  {
    name: "Sneha",
    city: "Pune",
    message: "found parking instantly with ParkGanga app",
    timeAgo: "3 minutes ago"
  },
  {
    name: "Vikram",
    city: "Hyderabad",
    message: "ko parking spot milne me sirf 2 minute lage",
    timeAgo: "1 minute ago"
  },
  {
    name: "Ananya",
    city: "Gurgaon",
    message: "booked premium parking spot in just 30 seconds",
    timeAgo: "just now"
  },
  {
    name: "Rohan",
    city: "Noida",
    message: "ne ParkGanga se ₹150 bachaye parking fees me",
    timeAgo: "4 minutes ago"
  },
  {
    name: "Kavita",
    city: "Chandigarh",
    message: "ने पार्कगंगा से मॉल में पार्किंग की टेंशन खत्म की",
    timeAgo: "2 minutes ago"
  },
  {
    name: "Aryan",
    city: "Lucknow",
    message: "found parking at the crowded market in 5 minutes",
    timeAgo: "just now"
  },
  {
    name: "Sanjay",
    city: "Indore",
    message: "ko office ke pass parking spot mil gaya turant",
    timeAgo: "3 minutes ago"
  },
  {
    name: "Neha",
    city: "Chennai",
    message: "ने पार्कगंगा से पार्किंग स्पॉट आसानी से पाया",
    timeAgo: "4 minutes ago"
  },
  {
    name: "Arjun",
    city: "Kolkata",
    message: "saved ₹200 on parking fees using ParkGanga",
    timeAgo: "just now"
  },
  {
    name: "Meera",
    city: "Jaipur",
    message: "ne ParkGanga app download karke 30 minute bachaye",
    timeAgo: "7 minutes ago"
  },
  {
    name: "Rajesh",
    city: "Ahmedabad",
    message: "ने पार्कगंगा ऐप से पार्किंग की टेंशन खत्म की",
    timeAgo: "2 minutes ago"
  },
  {
    name: "Pooja",
    city: "Bhopal",
    message: "found VIP parking spot using ParkGanga premium",
    timeAgo: "1 minute ago"
  },
  {
    name: "Karan",
    city: "Surat",
    message: "ne shopping mall me parking ke liye line skip ki",
    timeAgo: "just now"
  },
  {
    name: "Divya",
    city: "Nagpur",
    message: "ने पार्कगंगा से अपने दोस्तों को भी रेफर किया",
    timeAgo: "5 minutes ago"
  },
  {
    name: "Ravi",
    city: "Patna",
    message: "booked weekly parking pass and saved ₹500",
    timeAgo: "3 minutes ago"
  },
  {
    name: "Anjali",
    city: "Kochi",
    message: "ko ParkGanga se perfect parking spot mil gaya",
    timeAgo: "2 minutes ago"
  },
  {
    name: "Suresh",
    city: "Vadodara",
    message: "ने पार्कगंगा ऐप से अपनी कार सुरक्षित पार्क की",
    timeAgo: "just now"
  }
];

const NotificationBubble = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentNotification, setCurrentNotification] = useState(null);
  
  // Function to get a random notification
  const getRandomNotification = () => {
    const randomIndex = Math.floor(Math.random() * notificationData.length);
    return notificationData[randomIndex];
  };
  
  useEffect(() => {
    // Show first notification immediately when component mounts
    setCurrentNotification(getRandomNotification());
    setIsVisible(true);
    
    // Hide first notification after 8 seconds
    const hideFirstTimer = setTimeout(() => {
      setIsVisible(false);
    }, 6000);
    
    // Set up interval to show and hide notifications
    const showIntervalId = setInterval(() => {
      // Show a new notification
      setCurrentNotification(getRandomNotification());
      setIsVisible(true);
      
      // Hide it after 8 seconds
      setTimeout(() => {
        setIsVisible(false);
      }, 6000);
      
    }, 16000); // Start a new notification cycle every 16 seconds
    
    return () => {
      clearTimeout(hideFirstTimer);
      clearInterval(showIntervalId);
    };
  }, []);
  
  if (!currentNotification) return null;
  
  return (
    <BubbleContainer $isVisible={isVisible}>
      <GlassBubble>
        <IconContainer>
          <FaUser />
        </IconContainer>
        <ContentContainer>
          <UserName>{currentNotification.name} from {currentNotification.city}</UserName>
          <Message>{currentNotification.message}</Message>
          <TimeAgo>{currentNotification.timeAgo}</TimeAgo>
        </ContentContainer>
      </GlassBubble>
    </BubbleContainer>
  );
};

export default NotificationBubble;